import type {
  ContractDocumentType,
  ContractRenewalStatus,
  ContractState,
  ContractType,
  ContractView,
} from "@/api/types";

export type DsTone = "primary" | "amber" | "destructive" | "muted" | "neutral" | "success";

export const STATE_LABEL: Record<ContractState, string> = {
  draft: "Чернетка",
  pending_approval_sales: "На погодженні (sales)",
  pending_approval_legal: "На погодженні (legal/finance)",
  approved: "Очікує відправлення",
  sent_to_client: "Очікує підпису",
  signed: "Підписано",
  active: "Активний",
  expired: "Прострочений",
  terminated: "Розірваний",
  archived: "В архіві",
  rejected: "Відхилений клієнтом",
  inactive: "Неактивний",
};

export const STATE_TONE: Record<ContractState, DsTone> = {
  draft: "muted",
  pending_approval_sales: "amber",
  pending_approval_legal: "amber",
  approved: "primary",
  sent_to_client: "amber",
  signed: "success",
  active: "success",
  expired: "destructive",
  terminated: "destructive",
  archived: "muted",
  rejected: "destructive",
  inactive: "muted",
};

/** When state=draft AND lastRejectedById set, swap the label/tone. */
export function resolveStateLabel(
  state: ContractState,
  lastRejectedById: string | null,
): { label: string; tone: DsTone } {
  if (state === "draft" && lastRejectedById) {
    return { label: "На правці", tone: "amber" };
  }
  return { label: STATE_LABEL[state] ?? state, tone: STATE_TONE[state] ?? "muted" };
}

export const RENEWAL_STATUS_LABEL: Record<ContractRenewalStatus, string> = {
  ok: "OK",
  expiring_soon: "Спливає (≤90 днів)",
  expiring_critical: "Спливає (≤30 днів)",
  renewal_overdue: "Прострочено поновлення",
  renewal_in_progress: "Поновлення в процесі",
  renewed: "Поновлений",
  not_renewing: "Не поновлювати",
  terminated_planned: "Заплановано розірвання",
};

export const RENEWAL_STATUS_TONE: Record<ContractRenewalStatus, DsTone> = {
  ok: "muted",
  expiring_soon: "amber",
  expiring_critical: "destructive",
  renewal_overdue: "destructive",
  renewal_in_progress: "primary",
  renewed: "success",
  not_renewing: "muted",
  terminated_planned: "destructive",
};

export const CONTRACT_TYPE_LABEL: Record<ContractType, string> = {
  supply_agreement: "Договір постачання",
  dealer_agreement: "Дилерський договір",
  marketing_agreement: "Маркетинговий договір",
  nda: "NDA",
  other: "Інше",
};

export const DOCUMENT_TYPE_LABEL: Record<ContractDocumentType, string> = {
  draft_contract: "Чернетка договору",
  main_contract: "Договір",
  signed_scan: "Підписаний скан",
  appendix: "Додаток",
  additional_agreement: "Додаткова угода",
  specification: "Специфікація",
  protocol_of_disagreements: "Протокол розбіжностей",
  termination_notice: "Повідомлення про розірвання",
  amendment: "Поправка",
  other: "Інше",
};

export interface ViewDef {
  k: ContractView;
  label: string;
  counterKey?: keyof import("@/api/types").ContractListCounters;
}

export const VIEW_DEFS: ViewDef[] = [
  { k: "my_action", label: "Потребує дії", counterKey: "myAction" },
  { k: "all", label: "Усі" },
  { k: "active", label: "Активні" },
  { k: "expiring", label: "Спливають", counterKey: "expiring" },
  { k: "pending_all", label: "На погодженні", counterKey: "pendingAll" },
  { k: "pending_signature", label: "Очікують підпису", counterKey: "pendingSignature" },
  { k: "draft", label: "Чернетки" },
];

export interface RenewModeDef {
  k: "extend" | "new_version" | "upload";
  label: string;
  description: string;
  taskLabel: string;
}

export const RENEW_MODE_DEFS: RenewModeDef[] = [
  {
    k: "extend",
    label: "Подовжити",
    description: "Авто-продовження поточного договору на 1 рік.",
    taskLabel: "Перевірити подовжений договір",
  },
  {
    k: "new_version",
    label: "Нова версія",
    description: "Створити пов'язаний договір на нових умовах.",
    taskLabel: "Підготувати нову версію",
  },
  {
    k: "upload",
    label: "Завантажити підписану",
    description: "Прикріпити підписану додаткову угоду до договору.",
    taskLabel: "Перевірити підписану додаткову угоду",
  },
];

/** Date helper for renewal banner / detail headers. */
export function formatDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("uk-UA", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return iso;
  }
}
